/**
 * Einstellungen. Leitung liest, Admin bearbeitet.
 *  - Integrationen und Schalter: nur lesend, Werte kommen aus der .env (Ruling R2).
 *  - Aussteller (Firmendaten auf Belegen): Admin bearbeitet; gilt nur für NEUE Belege,
 *    festgeschriebene Belege behalten ihre Daten (GoBD).
 *  - Betrieb: Cut-off, automatischer Tagesabschluss, Beilagenregeln (Admin).
 *  - Verpackungen: lesend. Benutzer: Admin verwaltet (anlegen, Rolle, deaktivieren, Passwort), Leitung liest (B-S1).
 *  - Umschalttag (N2/R71): eigene Seite /einstellungen/umschaltung (Vorschau Leitung, Durchführen Admin).
 *  - Versand / Tarife und Abholaufträge (R112–R124): /einstellungen/versand und /einstellungen/abholungen.
 */
import { useState } from 'react';
import { Link, Route, Routes } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BEILAGEN_BEDINGUNGEN, type BeilagenBedingung } from '@storeos/contracts';
import { post, systemStatus } from '../api';
import { aussteller as ladeAussteller, ausstellerAendern, nummernkreise, nummernkreisSetzen } from '../api/belege';
import { einstellungen, einstellungenAendern, integrationenStatus } from '../api/protokoll';
import { mockModus, mockZuruecksetzen } from '../api/mock';
import type { AusstellerAenderung, AusstellerZeile, Beilagenregel, Einstellungen } from '../api/typen';
import { datum, datumZeit } from '../components/format';
import { Darf, Dialog, DialogFormular, Feld, Karte, Seitenkopf, Tabelle, useDarf, useToast, Zustand } from '../components/ui';
import { BenutzerKarte } from './Benutzer';
import { UmschaltungSeite } from './Umschaltung';
import { AbholungenSeite, VersandTarifeSeite } from './Versand';

const BEDINGUNG_TEXT: Record<BeilagenBedingung, string> = { immer: 'immer', geschenk: 'Lieferadresse ≠ Rechnungsadresse', ausland: 'Lieferland ≠ DE' };

export function EinstellungenSeite() {
  return (
    <Routes>
      <Route index element={<EinstellungenUebersicht />} />
      <Route path="umschaltung" element={<UmschaltungSeite />} />
      <Route path="versand" element={<VersandTarifeSeite />} />
      <Route path="abholungen" element={<AbholungenSeite />} />
    </Routes>
  );
}

function EinstellungenUebersicht() {
  const qc = useQueryClient();
  const toast = useToast();
  const darf = useDarf();
  const sys = useQuery({ queryKey: ['system-status'], queryFn: systemStatus });
  const integ = useQuery({ queryKey: ['integrationen-status'], queryFn: integrationenStatus });
  const e = useQuery({ queryKey: ['einstellungen'], queryFn: einstellungen });
  const aus = useQuery({ queryKey: ['aussteller'], queryFn: ladeAussteller });
  const [ausstellerEdit, setAusstellerEdit] = useState<AusstellerZeile | null>(null);
  const [betriebEdit, setBetriebEdit] = useState(false);
  const wooMock = sys.data?.integrationen.find((i) => i.name === 'woo')?.mode === 'mock';

  return (
    <section>
      <Seitenkopf titel="Einstellungen" unter={darf('admin') ? 'Integrationen kommen aus der .env und sind hier nur sichtbar. Aussteller und Betrieb kannst du als Admin bearbeiten.' : 'Nur lesend. Ändern darf die Rolle admin.'} />
      <div className="raster-2">
        <Karte titel="Integrationen (.env, nur lesend)">
          <Zustand laden={sys.isLoading} fehler={sys.error}>
            {sys.data && (
              <>
                <Tabelle zeilen={sys.data.integrationen} schluessel={(i) => i.name} testid="integrationen-tabelle"
                  spalten={[
                    { kopf: 'Integration', zelle: (i) => i.name },
                    { kopf: 'Modus', zelle: (i) => <span className={`badge modus-${i.mode}`}>{i.mode === 'fehler' ? 'FEHLER' : i.mode === 'aus' ? 'AUS' : i.mode === 'mock' ? 'MOCK' : 'LIVE'}</span> },
                    { kopf: 'Hinweis', zelle: (i) => <span className="hinweis">{i.hinweis}</span>, klasse: 'umbruch' },
                  ]} />
                <dl className="werte">
                  <dt>Umgebung</dt><dd>{sys.data.env === 'live' ? 'LIVE' : 'TEST (Belege mit TEST-)'}</dd>
                  <dt>Carrier</dt><dd data-testid="carrier-einstellung">{integ.data?.versand ? <>primär {integ.data.versand.primaer.toUpperCase()} · Fallback {integ.data.versand.fallback ? integ.data.versand.fallback : 'keiner'} <small className="hinweis">CARRIER_PRIMAER / CARRIER_FALLBACK, Fallback nur bei technischem Fehler</small></> : sys.data.carrierDefault}</dd>
                  {integ.data?.versand && <><dt>Label</dt><dd>{integ.data.versand.labelFormat === 'pdf_a5' ? 'A5 (PDF)' : integ.data.versand.labelFormat} · Versand nur nach {integ.data.versand.versandLaender.join(', ')} <small className="hinweis">LABEL_FORMAT, VERSAND_LAENDER</small></dd></>}
                  {integ.data?.druck && <><dt>Druck</dt><dd data-testid="druck-einstellung">PrintNode {integ.data.druck.modus === 'live' ? 'LIVE' : integ.data.druck.modus === 'mock' ? 'MOCK' : 'GESPERRT'} · Label {integ.data.druck.labelAutomatisch ? 'automatisch' : 'manuell'}{Object.entries(integ.data.druck.ziele).map(([art, z]) => <span key={art}><br />{art}: Drucker {z.druckerId ?? '–'} · Fach {z.fach ?? 'Standard'} · {z.papier ?? 'Standardpapier'}</span>)}</dd></>}
                  {integ.data?.partnerWebhook && <><dt>Partner-Tracking</dt><dd>Webhook <code>{integ.data.partnerWebhook.pfad}</code> {integ.data.partnerWebhook.modus === 'live' ? '(Token gesetzt)' : '(ohne Token: aus, Mock-Route und CSV-Import)'}</dd></>}
                  <dt>Woo-Schreiben</dt><dd>Status {sys.data.wooSchreiben.status ? 'AN' : 'aus'} · Notiz {sys.data.wooSchreiben.notiz ? 'AN' : 'aus'} · Bestand {sys.data.wooSchreiben.bestand ? 'AN' : 'aus'} <small className="hinweis">(aus = Trockenlauf)</small></dd>
                  <dt>BHB-Debitorenkonto</dt><dd data-testid="bhb-debitor">{integ.data ? (integ.data.buchhaltung.debitorKonto ?? 'nicht senden') : '…'} <small className="hinweis">BHB_DEBITOR_KONTO</small></dd>
                  <dt>Datenbank</dt><dd>{sys.data.db.dialekt} · {sys.data.db.migrationen} Migrationen</dd>
                  <dt>Version</dt><dd>{sys.data.version}</dd>
                </dl>
                <p className="hinweis">Ändern: Variable in <code>storeos/.env</code> setzen, API neu starten. Reihenfolge und Trockenlauf: README.</p>
              </>
            )}
          </Zustand>
          {integ.data && (
            <>
              <h3>Kanäle</h3>
              <ul className="liste-einfach">
                {integ.data.stores.map((s) => <li key={s.id}><span className="mono">{s.code}</span><span>{s.typ} · {s.adapter === 'mock' ? 'MOCK' : 'LIVE'}</span><span className="hinweis">Cursor {datumZeit(s.sync_cursor)}</span></li>)}
              </ul>
              <h3>Jobs und letzte Läufe</h3>
              <p className="hinweis">{integ.data.jobs.join(' · ')}</p>
              <ul className="liste-einfach">
                {integ.data.letzteLaeufe.map((l) => <li key={l.art}><span className="mono">{l.art}</span><span className={`badge erg-${l.ergebnis}`}>{l.ergebnis}</span><span className="hinweis kuerzen">{datumZeit(l.created_at)} · {l.meldung}</span></li>)}
              </ul>
              <h3>Outbox</h3>
              <ul className="liste-einfach">
                {integ.data.outbox.map((o) => <li key={`${o.ziel}-${o.status}`}><span className="mono">{o.ziel}</span><span className={`badge ob-${o.status}`}>{o.status}</span><span className="rechts">{o.anzahl}</span></li>)}
              </ul>
            </>
          )}
          {wooMock && (
            <Darf rolle="leitung">
              <div className="knopfreihe links">
                <button type="button" className="knopf-zweit" onClick={async () => {
                  try { const r = await post<{ externalId: string }>('/integrationen/mock/woo/webhook', { aktion: 'neu' }); toast(`Mock-Bestellung ${r.externalId} per Webhook importiert`); void qc.invalidateQueries(); } catch (err) { toast((err as Error).message, 'fehler'); }
                }}>Mock-Bestellung per Webhook erzeugen</button>
              </div>
            </Darf>
          )}
        </Karte>

        <Karte titel="Umschalttag Billbee → StoreOS" aktion={<Link to="/einstellungen/umschaltung" className="knopf-zweit klein" data-testid="link-umschaltung">Öffnen</Link>}>
          <p className="hinweis">Runbook 08, Schritt 4.3: Vorschau zu T0 (Leitung), Umschaltung durchführen (Admin, doppelte Bestätigung), danach je übernommenem Auftrag bestätigen, wer versendet. Automatisch läuft sie, sobald <code>STOREOS_UMSCHALTUNG_AB</code> erreicht ist.</p>
        </Karte>

        <Karte titel="Versand / Tarife" aktion={<span className="knopfreihe"><Link to="/einstellungen/versand" className="knopf-zweit klein" data-testid="link-versand">Tarife und Regeln</Link><Link to="/einstellungen/abholungen" className="knopf-zweit klein" data-testid="link-abholungen">Abholaufträge</Link></span>}>
          <p className="hinweis">Versandrouting Stufe 1 im Schattenbetrieb: StoreOS rechnet je Sendung, welche Option es gewählt hätte, und zeigt das im Auftrag. Produktwahl, Label und Tagesabschluss bleiben wie heute. Tarife mit Quelle je Zeile; Verzug, Selbsteinlieferung und Spitzentag ändert der Admin.</p>
        </Karte>

        <Karte titel="Aussteller (Firmendaten auf Belegen)">
          <Zustand laden={aus.isLoading} fehler={aus.error}>
            {aus.data?.map((i) => (
              <div key={i.id} className="aussteller" data-testid="aussteller">
                <dl className="werte">
                  <dt>Name</dt><dd data-testid="aussteller-name">{i.name}</dd>
                  <dt>Adresse</dt><dd>{i.adresse.strasse} {i.adresse.hausnummer}, {i.adresse.plz} {i.adresse.ort}, {i.adresse.land}</dd>
                  <dt>E-Mail / Telefon</dt><dd>{i.email ?? '–'} · {i.telefon ?? '–'}</dd>
                  <dt>USt-IdNr.</dt><dd>{i.ust_id ?? <span className="warn-text">fehlt</span>}</dd>
                  <dt>Steuernummer</dt><dd>{i.steuernummer ?? <span className="warn-text">fehlt</span>}</dd>
                  <dt>Register</dt><dd>{i.register ?? '–'}</dd>
                  <dt>Geschäftsführung</dt><dd>{i.geschaeftsfuehrung ?? '–'}</dd>
                  <dt>Bank</dt><dd>{i.bank ? `${i.bank.bank} · ${i.bank.iban}${i.bank.bic ? ` · ${i.bank.bic}` : ''}` : '–'}</dd>
                  <dt>Kleinunternehmer</dt><dd>{i.kleinunternehmer ? 'ja (§ 19 UStG)' : 'nein'}</dd>
                  <dt>Gültig ab</dt><dd>{datum(i.gueltig_ab)}</dd>
                  <dt>Belege</dt><dd>{i.belege}{i.letzter_beleg ? ` · zuletzt ${datum(i.letzter_beleg)}` : ''}</dd>
                </dl>
                {!i.ust_id && !i.steuernummer && <p className="warn-box">Ohne Steuernummer oder USt-IdNr. entstehen im Livemodus keine Belege.</p>}
                <Darf rolle="admin"><button type="button" className="knopf-zweit" onClick={() => setAusstellerEdit(i)} data-testid="aussteller-bearbeiten">Bearbeiten</button></Darf>
                <Nummernkreise issuerId={i.id} />
              </div>
            ))}
          </Zustand>
        </Karte>

        <Zustand laden={e.isLoading} fehler={e.error}>
          {e.data && (
            <>
              <Karte titel="Betrieb" aktion={<Darf rolle="admin"><button type="button" className="knopf-zweit klein" onClick={() => setBetriebEdit(true)} data-testid="betrieb-bearbeiten">Bearbeiten</button></Darf>}>
                <dl className="werte">
                  <dt>Marke</dt><dd>{e.data.marke.name} <small className="hinweis">({e.data.marke.code}) · Absender {e.data.marke.absender_name} &lt;{e.data.marke.absender_email}&gt;</small></dd>
                  <dt>Cut-off</dt><dd data-testid="cutoff">{e.data.cutoff} Uhr <small className="hinweis">DHL-Abholzeit noch offen (Jour fixe), Default 14:00</small></dd>
                  <dt>Auto-Abschluss</dt><dd>{e.data.tagesabschluss_auto} Uhr</dd>
                </dl>
                <h3>Beilagenregeln</h3>
                <Tabelle zeilen={e.data.beilagenregeln} schluessel={(b) => b.id}
                  spalten={[
                    { kopf: 'Regel', zelle: (b) => b.name },
                    { kopf: 'Wann', zelle: (b) => BEDINGUNG_TEXT[b.bedingung] ?? b.bedingung },
                    { kopf: 'Beilage', zelle: (b) => b.beilage },
                    { kopf: 'Aktiv', zelle: (b) => (b.aktiv ? 'ja' : 'nein') },
                  ]} />
              </Karte>
              <Karte titel="Verpackungen">
                <Tabelle zeilen={e.data.verpackungen} schluessel={(v) => v.id}
                  spalten={[
                    { kopf: 'Nr', zelle: (v) => v.nr },
                    { kopf: 'Name', zelle: (v) => v.name },
                    { kopf: 'Innenmaß (mm)', zelle: (v) => (v.innen_l_mm ? `${v.innen_l_mm} × ${v.innen_b_mm} × ${v.innen_h_mm}` : 'manuell') },
                    { kopf: 'Leergewicht', zelle: (v) => `${v.leergewicht_g} g`, rechts: true },
                  ]} />
              </Karte>
              <BenutzerKarte nurLesend={e.data.benutzer} />
            </>
          )}
        </Zustand>

        {mockModus() === 'erzwungen' && (
          <Karte titel="Mock-Schicht der SPA">
            <p className="hinweis">Modus: erzwungen (VITE_API_MOCK=1, keine API). Die Mock-Daten liegen im Tab (sessionStorage).</p>
            <button type="button" className="knopf-zweit" onClick={async () => { await mockZuruecksetzen(); toast('Mock-Daten zurückgesetzt'); void qc.invalidateQueries(); }}>Mock-Daten zurücksetzen</button>
          </Karte>
        )}
      </div>

      <Dialog offen={!!ausstellerEdit} titel="Aussteller bearbeiten" onSchliessen={() => setAusstellerEdit(null)} breit>
        {ausstellerEdit && <AusstellerFormular a={ausstellerEdit} onAbbrechen={() => setAusstellerEdit(null)}
          onFertig={() => { setAusstellerEdit(null); toast('Aussteller gespeichert. Gilt für neue Belege.'); void qc.invalidateQueries({ queryKey: ['aussteller'] }); }} />}
      </Dialog>
      <Dialog offen={betriebEdit} titel="Betrieb bearbeiten" onSchliessen={() => setBetriebEdit(false)} breit>
        {betriebEdit && e.data && <BetriebFormular e={e.data} onAbbrechen={() => setBetriebEdit(false)}
          onFertig={(neu) => { setBetriebEdit(false); qc.setQueryData(['einstellungen'], neu); toast('Gespeichert'); }} />}
      </Dialog>
    </section>
  );
}

function AusstellerFormular({ a, onAbbrechen, onFertig }: { a: AusstellerZeile; onAbbrechen: () => void; onFertig: () => void }) {
  const [w, setW] = useState<Record<string, string>>({
    name: a.name, strasse: a.adresse.strasse, hausnummer: a.adresse.hausnummer, plz: a.adresse.plz, ort: a.adresse.ort, land: a.adresse.land,
    email: a.email ?? '', telefon: a.telefon ?? '', ust_id: a.ust_id ?? '', steuernummer: a.steuernummer ?? '', register: a.register ?? '',
    geschaeftsfuehrung: a.geschaeftsfuehrung ?? '', iban: a.bank?.iban ?? '', bic: a.bank?.bic ?? '', bank: a.bank?.bank ?? '', gueltig_ab: a.gueltig_ab,
  });
  const [klein, setKlein] = useState(a.kleinunternehmer);
  const feld = (k: string, label: string, testid?: string) => (
    <Feld label={label}><input value={w[k] ?? ''} onChange={(e) => setW({ ...w, [k]: e.target.value })} name={k} data-testid={testid} /></Feld>
  );
  return (
    <DialogFormular absendenText="Speichern" onAbbrechen={onAbbrechen} onAbsenden={async () => {
      const d: AusstellerAenderung = {
        name: w.name.trim(), adresse: { strasse: w.strasse.trim(), hausnummer: w.hausnummer.trim(), plz: w.plz.trim(), ort: w.ort.trim(), land: w.land.trim().toUpperCase() },
        email: w.email.trim() || null, telefon: w.telefon.trim() || null, ust_id: w.ust_id.trim().toUpperCase().replace(/\s/g, '') || null, steuernummer: w.steuernummer.trim() || null,
        register: w.register.trim() || null, geschaeftsfuehrung: w.geschaeftsfuehrung.trim() || null,
        bank: w.iban.trim() ? { iban: w.iban.replace(/\s/g, ''), bic: w.bic.trim() || null, bank: w.bank.trim() } : null,
        kleinunternehmer: klein, gueltig_ab: w.gueltig_ab,
      };
      await ausstellerAendern(a.id, d);
      onFertig();
    }}>
      <p className="warn-box">Änderungen gelten nur für <strong>neue</strong> Belege. Die {a.belege} bereits festgeschriebenen Belege behalten ihre Ausstellerdaten unverändert (GoBD); eine Korrektur geht nur per Gutschrift.</p>
      <div className="raster-felder">
        {feld('name', 'Name', 'aussteller-feld-name')}{feld('email', 'E-Mail')}{feld('telefon', 'Telefon')}
        {feld('strasse', 'Straße')}{feld('hausnummer', 'Hausnummer')}{feld('plz', 'PLZ')}
        {feld('ort', 'Ort')}{feld('land', 'Land (ISO)')}{feld('gueltig_ab', 'Gültig ab (JJJJ-MM-TT)')}
        {feld('ust_id', 'USt-IdNr.', 'aussteller-feld-ust')}{feld('steuernummer', 'Steuernummer')}{feld('register', 'Register')}
        {feld('geschaeftsfuehrung', 'Geschäftsführung')}{feld('iban', 'IBAN')}{feld('bic', 'BIC')}
        {feld('bank', 'Bank')}
      </div>
      <label className="inline"><input type="checkbox" checked={klein} onChange={(e) => setKlein(e.target.checked)} /> Kleinunternehmer (§ 19 UStG)</label>
    </DialogFormular>
  );
}

function BetriebFormular({ e, onAbbrechen, onFertig }: { e: Einstellungen; onAbbrechen: () => void; onFertig: (neu: Einstellungen) => void }) {
  const [cutoff, setCutoff] = useState(e.cutoff);
  const [auto, setAuto] = useState(e.tagesabschluss_auto);
  const [regeln, setRegeln] = useState<Beilagenregel[]>(e.beilagenregeln);
  const setze = (i: number, teil: Partial<Beilagenregel>) => setRegeln(regeln.map((r, j) => (j === i ? { ...r, ...teil } : r)));
  return (
    <DialogFormular absendenText="Speichern" onAbbrechen={onAbbrechen} onAbsenden={async () => {
      onFertig(await einstellungenAendern({ cutoff, tagesabschluss_auto: auto, beilagenregeln: regeln.filter((r) => r.name.trim() && r.beilage.trim()) }));
    }}>
      <div className="raster-felder">
        <Feld label="Cut-off (HH:MM)"><input value={cutoff} onChange={(x) => setCutoff(x.target.value)} data-testid="feld-cutoff" /></Feld>
        <Feld label="Auto-Tagesabschluss (HH:MM)"><input value={auto} onChange={(x) => setAuto(x.target.value)} /></Feld>
      </div>
      <h3>Beilagenregeln</h3>
      <table className="tabelle">
        <thead><tr><th>Name</th><th>Wann</th><th>Beilage</th><th>Aktiv</th></tr></thead>
        <tbody>
          {regeln.map((r, i) => (
            <tr key={r.id}>
              <td><input value={r.name} onChange={(x) => setze(i, { name: x.target.value })} /></td>
              <td><select value={r.bedingung} onChange={(x) => setze(i, { bedingung: x.target.value as BeilagenBedingung })}>{BEILAGEN_BEDINGUNGEN.map((b) => <option key={b} value={b}>{BEDINGUNG_TEXT[b]}</option>)}</select></td>
              <td><input value={r.beilage} onChange={(x) => setze(i, { beilage: x.target.value })} /></td>
              <td><input type="checkbox" checked={r.aktiv} onChange={(x) => setze(i, { aktiv: x.target.checked })} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" className="knopf-flach" onClick={() => setRegeln([...regeln, { id: `regel-${Date.now()}`, name: '', bedingung: 'immer', beilage: '', aktiv: true }])}>Regel hinzufügen</button>
    </DialogFormular>
  );
}

const KREIS_TEXT: Record<string, string> = { RE: 'Rechnungen', GS: 'Gutschriften', LS: 'Lieferscheine' };

/**
 * R105: Nummernkreise wie bisher bei Billbee (fortlaufend ohne Jahr). Startwert = nächste Billbee-Nummer am
 * Umschalttag; änderbar nur, solange der Kreis keinen Live-Beleg hat (dann festgeschrieben, GoBD).
 */
function Nummernkreise({ issuerId }: { issuerId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const darf = useDarf();
  const q = useQuery({ queryKey: ['nummernkreise', issuerId], queryFn: () => nummernkreise(issuerId) });
  const [start, setStart] = useState<Record<string, string>>({});
  async function setzen(kreis: 'RE' | 'GS', format: 'fortlaufend' | 'jahr') {
    try {
      const wert = start[kreis];
      if (format === 'fortlaufend' && !/^\d+$/.test(wert ?? '')) throw new Error('Startwert: ganze Zahl (nächste Billbee-Nummer)');
      await nummernkreisSetzen(issuerId, kreis, format === 'fortlaufend' ? { format, start: Number(wert) } : { format });
      toast(`Nummernkreis ${KREIS_TEXT[kreis]} gespeichert`);
      void qc.invalidateQueries({ queryKey: ['nummernkreise', issuerId] });
    } catch (e) { toast((e as Error).message, 'fehler'); }
  }
  return (
    <div data-testid="nummernkreise">
      <h3>Nummernkreise</h3>
      <Zustand laden={q.isLoading} fehler={q.error}>
        <ul className="liste-einfach">
          {q.data?.map((k) => (
            <li key={k.kreis} data-testid={`nummernkreis-${k.kreis}`}>
              <span>{KREIS_TEXT[k.kreis]} ({k.kreis})</span>
              <span className="hinweis">{k.format === 'fortlaufend' ? `fortlaufend${k.praefix ? `, Präfix „${k.praefix}“` : ', ohne Präfix'}, Start ${k.start ?? 'offen'}` : 'mit Jahr (KREIS-JJJJ-NNNNN)'}</span>
              <span className="rechts mono">{k.naechste_live ? `nächste ${k.naechste_live}` : <span className="warn-text">Startwert fehlt</span>}{k.gesperrt ? ' · festgeschrieben' : ''}</span>
              {darf('admin') && !k.gesperrt && k.kreis !== 'LS' && (
                <span className="zeile">
                  <input type="number" min={1} placeholder="Start" value={start[k.kreis] ?? ''} onChange={(e) => setStart({ ...start, [k.kreis]: e.target.value })} style={{ width: '7rem' }} data-testid={`nummernkreis-start-${k.kreis}`} />
                  <button type="button" className="knopf-zweit klein" onClick={() => setzen(k.kreis as 'RE' | 'GS', 'fortlaufend')}>Startwert setzen</button>
                </span>
              )}
            </li>
          ))}
        </ul>
        <p className="hinweis">Am Umschalttag die nächste Billbee-Nummer als Start setzen (oder <code>npm run nummernkreis:start -- --kreis RE --ab &lt;Nummer&gt;</code>). Nach dem ersten Live-Beleg sind Format und Start festgeschrieben.</p>
      </Zustand>
    </div>
  );
}
